import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Who is asking for the tab bar to be hidden.
 *
 * A counter rather than a boolean: the thread hides the bar, and a voice call
 * started inside that thread hides it again. With a boolean the call ending
 * would put the bar back while the conversation is still full-screen. Each
 * caller gets a release function and the bar returns when the last one lets go.
 */
export const TabBarContext = createContext(null);

export function TabBarProvider({ children }) {
  const [hidden, setHidden] = useState(false);
  const holders = useRef(0);

  const requestHide = useCallback(() => {
    holders.current += 1;
    setHidden(true);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      holders.current = Math.max(0, holders.current - 1);
      if (holders.current === 0) setHidden(false);
    };
  }, []);

  /* index.css reserves the bar's height at the bottom of the page. That
     reservation has to come and go with the bar itself, or a screen that hides
     it keeps a strip of dead space where the bar is not — so the state is
     published to the body and the stylesheet reads it there. One attribute,
     written from the one place that knows. */
  useEffect(() => {
    document.body.dataset.tabbarHidden = hidden ? "true" : "false";
    return () => { delete document.body.dataset.tabbarHidden; };
  }, [hidden]);

  const value = useMemo(() => ({ hidden, requestHide }), [hidden, requestHide]);
  return <TabBarContext.Provider value={value}>{children}</TabBarContext.Provider>;
}

export default TabBarContext;
